import { useBoolean } from '@fluentui/react-hooks'
import {
    Button,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridRow,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    TableColumnDefinition,
    Title2,
    tokens,
    Toolbar,
    ToolbarButton,
    ToolbarGroup,
    Tooltip,
} from '@fluentui/react-components'
import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import {
    AddFilled,
    AddRegular,
    bundleIcon,
    DeleteDismissFilled,
    DeleteDismissRegular,
    PauseFilled,
    PauseRegular,
    PlayFilled,
    PlayRegular,
    SaveFilled,
    SaveRegular,
    TimerFilled,
    TimerRegular,
} from '@fluentui/react-icons'
import * as UUID from 'uuid'
import { DateTime, Interval } from 'luxon'
import { formatTimeInSeconds } from '../../helpers/numberHelper'
import { COMMAND_PREFIX } from '../../pages/Commands'
import { Command } from '../../config/appConfig'
import { TimeMutationDto } from '../../config/registrationConfig'
import { useHistory } from 'react-router-dom'
import FilePicker from '../TagPicker/FilePicker'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    toolbar: {
        paddingLeft: '8px',
        paddingRight: '8px',
        justifyContent: 'space-between',
        minWidth: '300px',
    },
    borderBottom: {
        borderBottomWidth: tokens.strokeWidthThin,
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
    },
    gridCell: {
        flex: 'unset',
    },
    gridCellFileId: {
        flex: '1 1 auto',
    },
    gridRowPaused: {
        opacity: '70%',
        '&:hover': {
            opacity: '100%',
        },
    },
    empty: {
        display: 'flex',
        padding: '0px 10px',
        minHeight: '45px',
        alignItems: 'center',
    },
})

type Timer = {
    id: string
    isRunning: boolean
    isSaved: boolean
    intervals: Interval[]
    fileId?: string
}

type TimerJson = {
    id: string
    isRunning: boolean
    isSaved: boolean
    intervals: IntervalJson[]
    fileId?: string
}

type IntervalJson = {
    start: string
    end: string
}

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DismissIcon = bundleIcon(DeleteDismissFilled, DeleteDismissRegular)
const PauseIcon = bundleIcon(PauseFilled, PauseRegular)
const PlayIcon = bundleIcon(PlayFilled, PlayRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const TimerIcon = bundleIcon(TimerFilled, TimerRegular)

const HammockTimersKey = 'hammock-timers'

const loadTimers = () => {
    const jsonTimers: TimerJson[] = JSON.parse(localStorage.getItem(HammockTimersKey) ?? '[]') || []

    return jsonTimers.map<Timer>(timer => ({
        id: timer.id,
        isRunning: timer.isRunning,
        isSaved: timer.isSaved,
        fileId: timer.fileId,
        intervals: timer.intervals.map<Interval>(interval =>
            Interval.fromDateTimes(DateTime.fromISO(interval.start).toUTC(), DateTime.fromISO(interval.end).toUTC()),
        ),
    }))
}

const calculateDuration = (timer: Timer): string => {
    return formatTimeInSeconds(
        timer.intervals
            .map(interval => {
                if (interval.end.equals(DateTime.fromISO('3000-01-01').toUTC())) {
                    return Interval.fromDateTimes(interval.start, DateTime.now())
                }
                return interval
            })
            .map(interval => interval.toDuration('seconds').seconds)
            .reduce((a, b) => a + b, 0),
    )
}

export function TimerDuration(props: { timer: Timer }): ReactElement {
    const [timer] = useState(() => props.timer)
    const [duration, setDuration] = useState(() => calculateDuration(timer))
    const durationTimeout = useRef<NodeJS.Timeout>()

    useEffect(() => {
        const updateDuration = () => {
            setDuration(() => calculateDuration(timer))
            durationTimeout.current = setTimeout(updateDuration, 1000)
        }
        updateDuration()

        return () => durationTimeout.current && clearTimeout(durationTimeout.current)
    }, [])

    return <div>{duration}</div>
}

export function Timers(): ReactElement {
    const history = useHistory()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const [isOpen, { toggle: toggleOpen }] = useBoolean(false)
    const [timers, setTimers] = useState<Timer[]>(() => loadTimers())

    useEffect(() => {
        window.addEventListener('storage', (evt: StorageEvent) => {
            if (evt.key === HammockTimersKey) {
                setTimers(loadTimers())
            }
        })
    }, [])

    useEffect(() => {
        console.log('timers changed, storing')
        localStorage.setItem(
            HammockTimersKey,
            JSON.stringify(
                timers.map<TimerJson>(timer => ({
                    id: timer.id,
                    isRunning: timer.isRunning,
                    isSaved: timer.isSaved,
                    fileId: timer.fileId,
                    intervals: timer.intervals.map<IntervalJson>(interval => ({
                        start: interval.start.toISO(),
                        end: interval.end.toISO(),
                    })),
                })),
            ),
        )
    }, [timers])

    const addTimer = (): void => {
        console.log('add timer')
        setTimers(a => [
            {
                id: UUID.v4(),
                isRunning: true,
                isSaved: false,
                intervals: [Interval.fromDateTimes(DateTime.now().toUTC(), DateTime.fromISO('3000-01-01').toUTC())],
            },
            ...a,
        ])
    }

    const pauseTimer = (id?: string): void => {
        console.log('pause timer')
        setTimers(a => {
            const endDateTime = DateTime.now().toUTC()

            a.filter(timer => timer.isRunning)
                .filter(timer => id === undefined || timer.id === id)
                .map(timer => {
                    const otherIntervals = timer.intervals?.filter(
                        interval => interval.end.diff(DateTime.now().toUTC()).toMillis() < 0,
                    )
                    const currentInterval = timer.intervals?.find(
                        interval => interval.end.diff(DateTime.now().toUTC()).toMillis() > 0,
                    )
                    timer.intervals = [...otherIntervals, Interval.fromDateTimes(currentInterval.start, endDateTime)]
                    timer.isRunning = false
                })

            return [...a]
        })
    }

    const playTimer = (id: string): void => {
        console.log('play timer')
        setTimers(a => {
            const startDateTime = DateTime.now().toUTC()
            const endDateTime = DateTime.fromISO('3000-01-01').toUTC()

            const otherTimers = a.filter(timer => timer.id !== id)
            const timer = a.find(timer => timer.id === id)

            timer.intervals = [...timer.intervals, Interval.fromDateTimes(startDateTime, endDateTime)]
            timer.isRunning = true

            return [timer, ...otherTimers]
        })
    }

    const saveTimer = (id: string): void => {
        console.log('save timer')
        const timer = timers.find(timer => timer.id === id)
        if (timer.isRunning) {
            pauseTimer(id)
        }

        const timeMutation: Partial<TimeMutationDto> = {
            fileId: timer.fileId,
            date: DateTime.now().toFormat('yyyy-LL-dd'),
            duration: calculateDuration(timer),
        }
        history.push(`${COMMAND_PREFIX}/${Command.AddTime}`, { data: timeMutation })

        timer.isSaved = true
        setTimers(a => {
            const otherTimers = a.filter(timer => timer.id !== id)
            const timer = a.find(timer => timer.id === id)

            return [...otherTimers, timer]
        })
    }

    const deleteTimer = (id?: string): void => {
        console.log('delete timer')
        setTimers(a => [...a.filter(timer => id !== undefined && timer.id !== id)])
    }

    const timerColumns: TableColumnDefinition<Timer>[] = [
        createTableColumn<Timer>({
            columnId: 'actions',
            renderCell: timer => {
                return (
                    <>
                        <Tooltip
                            content={timer.isRunning ? translate('timers.timer.pause') : translate('timers.timer.play')}
                            relationship="description"
                            withArrow
                        >
                            <Button
                                appearance={'subtle'}
                                icon={timer.isRunning ? <PauseIcon /> : <PlayIcon />}
                                disabled={timer.isSaved}
                                onClick={() => (timer.isRunning ? pauseTimer(timer.id) : playTimer(timer.id))}
                            />
                        </Tooltip>
                        <Tooltip content={translate('timers.timer.save')} relationship="description" withArrow>
                            <Button
                                appearance={'subtle'}
                                icon={<SaveIcon />}
                                disabled={timer.isSaved}
                                onClick={() => saveTimer(timer.id)}
                            />
                        </Tooltip>
                    </>
                )
            },
        }),
        createTableColumn<Timer>({
            columnId: 'fileId',
            renderCell: timer => {
                return (
                    <FilePicker
                        appearance={'underline'}
                        singleSelect
                        selectedOptions={[timer.fileId]}
                        onOptionsSelect={(_, options) => {
                            timer.fileId = options?.[0]
                            setTimers(a => [...a])
                        }}
                        placeholderPrefixKey={'base.tagpicker.placeholder.prefix_choose'}
                        placeholderKey={''}
                    />
                )
            },
        }),
        createTableColumn<Timer>({
            columnId: 'duration',
            renderCell: timer => {
                return <TimerDuration timer={timer} />
            },
        }),
        createTableColumn<Timer>({
            columnId: 'delete',
            renderCell: timer => {
                return (
                    <Tooltip content={translate('timers.timer.delete')} relationship="description" withArrow>
                        <Button appearance={'subtle'} icon={<DismissIcon />} onClick={() => deleteTimer(timer.id)} />
                    </Tooltip>
                )
            },
        }),
    ]

    return (
        <Popover withArrow closeOnIframeFocus={false} trapFocus={true} open={isOpen} onOpenChange={toggleOpen}>
            <PopoverTrigger disableButtonEnhancement>
                <Button icon={<TimerIcon />} appearance={'subtle'}>
                    {timers.filter(timer => timer.isRunning).length}
                </Button>
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <div>
                    <Title2 align="center">{translate('timers.title')}</Title2>
                    <div>
                        <Toolbar size="small" className={mergeClasses(styles.toolbar, styles.borderBottom)}>
                            <ToolbarGroup role="presentation">
                                <Tooltip content={translate('timers.add')} relationship="description" withArrow>
                                    <ToolbarButton icon={<AddIcon />} onClick={addTimer} />
                                </Tooltip>
                                <Tooltip content={translate('timers.pause')} relationship="description" withArrow>
                                    <ToolbarButton icon={<PauseIcon />} onClick={() => pauseTimer()} />
                                </Tooltip>
                            </ToolbarGroup>
                            <ToolbarGroup role="presentation">
                                <Tooltip content={translate('timers.delete')} relationship="description" withArrow>
                                    <ToolbarButton icon={<DismissIcon />} onClick={() => deleteTimer()} />
                                </Tooltip>
                            </ToolbarGroup>
                        </Toolbar>
                        <>
                            {(timers || []).length === 0 && (
                                <div className={mergeClasses(styles.empty, styles.borderBottom)}>
                                    {translate('timers.empty')}
                                </div>
                            )}

                            {(timers || []).length > 0 && (
                                <DataGrid items={timers} columns={timerColumns} getRowId={item => item.id}>
                                    <DataGridBody<Timer>>
                                        {({ item: timer, rowId }) => (
                                            <DataGridRow<Timer>
                                                key={rowId}
                                                className={!timer.isRunning && styles.gridRowPaused}
                                            >
                                                {props => (
                                                    <DataGridCell
                                                        key={rowId}
                                                        className={
                                                            props.columnId === 'fileId'
                                                                ? styles.gridCellFileId
                                                                : styles.gridCell
                                                        }
                                                    >
                                                        {props.renderCell(timer)}
                                                    </DataGridCell>
                                                )}
                                            </DataGridRow>
                                        )}
                                    </DataGridBody>
                                </DataGrid>
                            )}
                        </>
                    </div>
                </div>
            </PopoverSurface>
        </Popover>
    )
}
